import React, {StrictMode} from "react";
import {createRoot} from "react-dom/client";
import {
  BrowserRouter,
  Routes,
  Route,
  useLocation,
  useNavigationType,
  createRoutesFromChildren,
  matchRoutes,
} from "react-router-dom";
import {QueryClient, QueryClientProvider} from "@tanstack/react-query";
import {SuperTokensWrapper} from "supertokens-auth-react";
import {ThemeProvider} from "next-themes";
import * as Sentry from "@sentry/react";

import {initSuperTokens} from "@/lib/supertokens";
import {AuthPage} from "@/pages/auth-page";
import {AuthCallback} from "@/pages/auth-callback";
import {VerifyMagicLink} from "@/pages/verify-magic-link";
import {ProtectedRoute} from "@/components/protected-route";
import {RouterErrorBoundary} from "@/components/router-error-boundary";
import {Toaster} from "@/components/ui/sonner";
import App from "@/app";
import {VITE_API_DOMAIN, VITE_ENV, VITE_SENTRY_DSN} from "@/config";
import "./index.css";

if (VITE_SENTRY_DSN) {
  Sentry.init({
    dsn: VITE_SENTRY_DSN,
    environment: VITE_ENV,
    // Setting this option to true will send default PII data to Sentry.
    // For example, automatic IP address collection on events
    sendDefaultPii: true,
    // Enable logs to be sent to Sentry
    enableLogs: true,
    tunnel: `${VITE_API_DOMAIN}/api/tunnel`,
    integrations: [
      Sentry.reactRouterV7BrowserTracingIntegration({
        useEffect: React.useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
    ],
  });
}

const SentryRoutes = VITE_SENTRY_DSN ? Sentry.withSentryReactRouterV7Routing(Routes) : Routes;

// Initialize SuperTokens
initSuperTokens();

const queryClient = new QueryClient();

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <ThemeProvider attribute="class" defaultTheme="dark" enableSystem={false}>
      <SuperTokensWrapper>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter>
            <SentryRoutes>
              {/* Public routes */}
              <Route path="/auth" ErrorBoundary={RouterErrorBoundary} element={<AuthPage />} />
              <Route
                path="/auth/verify"
                ErrorBoundary={RouterErrorBoundary}
                element={<VerifyMagicLink />}
              />
              <Route
                path="/auth/callback/:providerId"
                ErrorBoundary={RouterErrorBoundary}
                element={<AuthCallback />}
              />
              {/* Protected routes */}
              <Route
                path="/*"
                ErrorBoundary={RouterErrorBoundary}
                element={
                  <ProtectedRoute>
                    <App />
                  </ProtectedRoute>
                }
              />
            </SentryRoutes>
          </BrowserRouter>
          <Toaster />
        </QueryClientProvider>
      </SuperTokensWrapper>
    </ThemeProvider>
  </StrictMode>
);
